import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PledgeClaimPage from '../pages/PledgeClaimPage';

const GroupClaimPage = () => {
  console.log('🏠 GroupClaimPage component function called');
  
  const { token } = useParams();
  console.log('🏠 GroupClaimPage token from useParams:', token);
  
  const navigate = useNavigate();
  console.log('🏠 GroupClaimPage navigate obtained');
  
  console.log('🏠 GroupClaimPage mounted with token:', token);
  
  const [isPledgeClaim, setIsPledgeClaim] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    console.log('🔄 useEffect triggered in GroupClaimPage');
    checkClaimType();
  }, [token]);

  const checkClaimType = async () => {
    try {
      setLoading(true);
      console.log('🔍 Checking claim type for token:', token);
      
      // First, try to fetch as a pledge claim
      const pledgeResponse = await fetch(`/api/pledges/claim/${token}`);
      console.log('📦 Pledge API response status:', pledgeResponse.status);
      
      if (pledgeResponse.ok) {
        // It's a pledge claim
        console.log('✅ Detected as PLEDGE claim');
        setIsPledgeClaim(true);
        setLoading(false);
        return;
      }
      
      // If not a pledge, try as a group takeover
      console.log('🔄 Not a pledge, checking group takeover...');
      const groupResponse = await fetch(`/api/takeover/${token}`);
      const groupData = await groupResponse.json();
      console.log('🏢 Group API response:', groupData);
      
      if (groupData.success) {
        console.log('✅ Detected as GROUP takeover claim');
        setIsPledgeClaim(false);
        setGroup(groupData.group);
      } else {
        console.log('❌ Invalid claim link');
        setIsPledgeClaim(false);
        setError(groupData.error || 'Invalid claim link');
      }
    } catch (error) {
      console.error('❌ Error checking claim type:', error);
      setIsPledgeClaim(false);
      setError('Please refresh the page to reload information');
    } finally {
      setLoading(false);
    }
  };

  // If it's a pledge claim, render the PledgeClaimPage component
  if (isPledgeClaim === true) {
    return <PledgeClaimPage claimCodeProp={token} />;
  }

  // Show loading state while checking claim type
  if (loading && isPledgeClaim === null) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #8b5cf6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Otherwise continue with group takeover logic
  const fetchGroupInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/takeover/${token}`);
      const data = await response.json();
      
      if (data.success) {
        setGroup(data.group);
      } else {
        setError(data.error || 'Invalid takeover link');
      }
    } catch (error) {
      setError('Please refresh the page to reload group information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setClaiming(true);
      setError('');
      
      const response = await fetch(`/api/takeover/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Auto-login the user
        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
        }
        
        // Show success message and redirect
        alert(`🎉 Welcome! You now own ${data.group.name}`);
        navigate('/dashboard');
      } else {
        if (data.action === 'signin') {
          setError(data.error);
          // Could redirect to sign in page here
        } else {
          setError(data.error || 'Failed to claim group');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading group information...</p>
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Invalid Link</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Group Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              {group?.logo_url ? (
                <img 
                  src={group.logo_url} 
                  alt={group.name}
                  className="w-16 h-16 rounded-full object-cover mr-3"
                />
              ) : (
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-xl font-bold">
                    {group?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Claim {group?.name}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  This group is ready for you!
                </p>
              </div>
            </div>
            
            {group?.description && (
              <p className="text-gray-600 text-sm mb-4">{group.description}</p>
            )}
            
            <p className="text-sm text-gray-500">
              Create your account to become the owner of this group
            </p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Your Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Create a password (min 6 characters)"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={claiming}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${claiming ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {claiming ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Claiming Group...
                </>
              ) : (
                `Claim ${group?.name} & Create Account`
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              By creating an account, you agree to our terms of service and privacy policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupClaimPage;
